import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Stack } from '@mui/material';
import { Flow } from '@thive/fddm-types';

import { useOrderProfileStore } from '@/modules/orderProfile/store/useOrderProfileStore';
import { FIELDS_SPACING } from '@/modules/common/constants/propertyPanel';

import { useFlowsUpdaters } from '../hooks/useFlowUpdaters';
import { isConsolidationFlowType } from '../guards';
import { InvalidFlow } from '../validation/types';
import { FlowDirection } from './FlowDirection';
import { FlowError } from './FlowError';
import { FlowsMultiEditField } from './FlowsMultiEditField';

const VEHICLE_LIMIT_CLEAR_BUTTON = { text: 'All', value: -1 };
const PARTIAL_SELECTION = 'interface:properties.flows.partial_selection';

const isConsolidationFlow = ({ type }: Flow) => isConsolidationFlowType(type);
const isConsolidationStorageFlow = ({ type }: Flow) => type === 'CONSOLIDATION_STORAGE';
const isConsolidationRetrievalFlow = ({ type }: Flow) => type === 'CONSOLIDATION_RETRIEVAL';
const isExternalFlow = ({ type }: Flow) => type === 'EXTERNAL';
const isGoodsToPersonsFlow = ({ type }: Flow) => type === 'GOODS_TO_PERSONS';
const hasVehicleLimit = ({ type }: Flow) => type === 'TRANSPORT' || isConsolidationFlowType(type);

const getLoads = ({ hourlyOrderCounts: [loads] }: Flow) => loads;
const getVehicleLimit = ({ vehicleLimit }: Flow) => vehicleLimit;
const getBatchSize = ({ consolidationStorageExtension }: Flow) =>
  consolidationStorageExtension?.batchSize;
const getShipmentSize = ({ consolidationRetrievalExtension }: Flow) =>
  consolidationRetrievalExtension?.shipmentSize;
const getNumConsolidations = ({
  consolidationStorageExtension,
  consolidationRetrievalExtension,
}: Flow) => (consolidationStorageExtension ?? consolidationRetrievalExtension)?.numConsolidations;
const getRetrievalStartDelay = ({ consolidationRetrievalExtension }: Flow) =>
  consolidationRetrievalExtension?.retrievalStartDelayInHours;
const getProcessDuration = ({ externalFlowExtension }: Flow) =>
  externalFlowExtension?.processDuration;

type Props = {
  flows: Flow[];
  /** The validation errors of all flows: the separate flow cards that show them are hidden */
  errorsByFlowId: ReadonlyMap<string, InvalidFlow>;
};

/**
 * @description Edits several selected flows at once. A field shows when any selected flow has it
 * and is only editable when all of them do (goods to persons has no fields of its own here).
 * Names and pre-announcement stay per flow: names are unique and pre-announcement depends on the
 * source of the flow.
 */
function FlowsMultiEditComponent({ flows, errorsByFlowId }: Props) {
  const { t } = useTranslation();
  const ids = useMemo(() => flows.map(({ id }) => id), [flows]);
  const {
    onFlowLoadsChange,
    onFlowVehicleLimitChange,
    onConsolidationGroupsChange,
    onBatchSizeChange,
    onShipmentSizeChange,
    onRetrievalStartDelayChange,
    onProcessDurationChange,
  } = useFlowsUpdaters(ids);
  const hasOrderProfile = useOrderProfileStore((state) => Boolean(state.orderProfile));

  // External flows don't take loads and, with an order profile, the loads come from its graph
  // (except for goods to persons)
  const hasLoads = useCallback(
    ({ type }: Flow) => type !== 'EXTERNAL' && (type === 'GOODS_TO_PERSONS' || !hasOrderProfile),
    [hasOrderProfile],
  );

  const hasGoodsToPersonsFlow = useMemo(() => flows.some(isGoodsToPersonsFlow), [flows]);

  const errors = useMemo(
    () =>
      flows.flatMap(({ id, name }) => {
        const error = errorsByFlowId.get(id);
        return error ? [{ id, name, error }] : [];
      }),
    [errorsByFlowId, flows],
  );

  return (
    <Stack spacing={FIELDS_SPACING} sx={{ mb: 2 }}>
      <FlowDirection flows={flows} />
      <FlowsMultiEditField
        flows={flows}
        labelKey='interface:properties.flows.flow_info.total_loads.label'
        reasonKey={`${PARTIAL_SELECTION}.only_with_loads`}
        hasField={hasLoads}
        getValue={getLoads}
        onChange={onFlowLoadsChange}
        minValue={1}
      />
      <FlowsMultiEditField
        flows={flows}
        labelKey='interface:properties.flows.consolidation.fields.batch_size.label'
        reasonKey={`${PARTIAL_SELECTION}.only_consolidation_storage`}
        hasField={isConsolidationStorageFlow}
        getValue={getBatchSize}
        onChange={onBatchSizeChange}
        minValue={1}
      />
      <FlowsMultiEditField
        flows={flows}
        labelKey='interface:properties.flows.consolidation.fields.shipment_size.label'
        reasonKey={`${PARTIAL_SELECTION}.only_consolidation_retrieval`}
        hasField={isConsolidationRetrievalFlow}
        getValue={getShipmentSize}
        onChange={onShipmentSizeChange}
        minValue={1}
      />
      <FlowsMultiEditField
        flows={flows}
        labelKey='interface:properties.flows.consolidation.fields.consolidation_groups.label'
        reasonKey={`${PARTIAL_SELECTION}.only_consolidation`}
        hasField={isConsolidationFlow}
        getValue={getNumConsolidations}
        onChange={onConsolidationGroupsChange}
        minValue={1}
      />
      <FlowsMultiEditField
        flows={flows}
        labelKey='interface:properties.flows.consolidation.fields.retrieval_start_delay.label'
        reasonKey={`${PARTIAL_SELECTION}.only_consolidation_retrieval`}
        hasField={isConsolidationRetrievalFlow}
        getValue={getRetrievalStartDelay}
        onChange={onRetrievalStartDelayChange}
        minValue={1}
        unit={t('interface:settings.units.hours')}
      />
      <FlowsMultiEditField
        flows={flows}
        labelKey='interface:properties.flows.manual.fields.process_duration.label'
        reasonKey={`${PARTIAL_SELECTION}.only_external`}
        hasField={isExternalFlow}
        getValue={getProcessDuration}
        onChange={onProcessDurationChange}
        minValue={0}
      />
      <FlowsMultiEditField
        flows={flows}
        labelKey='interface:properties.highway.vehicles'
        reasonKey={`${PARTIAL_SELECTION}.only_with_vehicle_limit`}
        hasField={hasVehicleLimit}
        getValue={getVehicleLimit}
        onChange={onFlowVehicleLimitChange}
        skipFocus
        clearButton={VEHICLE_LIMIT_CLEAR_BUTTON}
      />
      {hasGoodsToPersonsFlow && (
        <Alert severity='info'>{t('interface:properties.flows.goods_to_persons_info')}</Alert>
      )}
      {errors.map(({ id, name, error }) => (
        <FlowError key={id} error={error} flowName={name} />
      ))}
    </Stack>
  );
}

export const FlowsMultiEdit = memo(FlowsMultiEditComponent);
